import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import styled, { keyframes } from 'styled-components';
import { Profile, Project, Skill, Experience } from '../types';
import { dataService } from '../services/dataService';

interface DataContextType {
  profile: Profile;
  projects: Project[];
  skills: Skill[];
  experiences: Experience[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

interface DataProviderProps {
  children: ReactNode;
}

const DEFAULT_PROFILE: Profile = {
  name: '',
  location: '',
  email: '',
  profileImage: '/images/profile.jpg',
  bio: '',
  roles: 'Data Analyst',
  linkedin: '',
  greeting: "Hi, I'm",
  phone: '',
  title: '',
  cvUrl: '/downloads/ollie-gillyon-cv.pdf'
};

const ALLOWED_SKILL_CATEGORIES: Skill['category'][] = [
  'Programming & Query Languages',
  'Analytics & Data Platforms',
  'Business Systems and Tools',
  'Data experience'
];

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState<Profile>(DEFAULT_PROFILE);
  const [projects, setProjects] = useState<Project[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const getValidSkillCategory = useCallback((category?: string): Skill['category'] => {
    return ALLOWED_SKILL_CATEGORIES.includes(category as Skill['category'])
      ? (category as Skill['category'])
      : 'Programming & Query Languages';
  }, []);

  const sanitizeSkill = useCallback((skill: any, category: Skill['category']): Skill | null => {
    if (!skill || !skill.name) {
      return null;
    }

    const rawDots = typeof skill.dots === 'number'
      ? skill.dots
      : typeof skill.proficiency === 'number'
        ? skill.proficiency
        : 1;

    const dots = Math.min(3, Math.max(1, rawDots));

    return {
      name: String(skill.name),
      dots,
      category
    };
  }, []);

  const normalizeSkillsData = useCallback((data: any): Skill[] => {
    if (!data) {
      return [];
    }

    if (Array.isArray(data)) {
      return data
        .map(skill => sanitizeSkill(skill, getValidSkillCategory(skill?.category)))
        .filter((skill): skill is Skill => skill !== null);
    }

    if (Array.isArray(data.skills)) {
      return normalizeSkillsData(data.skills);
    }

    const skillsSource = data.skills && typeof data.skills === 'object'
      ? data.skills
      : typeof data === 'object'
        ? data
        : null;

    if (skillsSource && typeof skillsSource === 'object') {
      return Object.entries(skillsSource).flatMap(([category, items]) => {
        if (!Array.isArray(items)) {
          return [];
        }

        const validCategory = getValidSkillCategory(category);

        return items
          .map(item => sanitizeSkill(item, validCategory))
          .filter((skill): skill is Skill => skill !== null);
      });
    }

    return [];
  }, [getValidSkillCategory, sanitizeSkill]);

  const transformProjects = useCallback((items: any[]): Project[] => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter(item => item && item.title)
      .map((item, index) => ({
        id: typeof item.id === 'number' ? item.id : index + 1,
        title: String(item.title),
        description: String(item.description || ''),
        technologies: Array.isArray(item.technologies)
          ? item.technologies.map((tech: any) => String(tech))
          : [],
        category: item.category === 'dashboard' ? 'dashboard' : 'project',
        keyResults: item.keyResults ? String(item.keyResults) : undefined
      }));
  }, []);

  const transformExperiences = useCallback((items: any[]): Experience[] => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter(item => item && item.title)
      .map((item, index) => {
        const type: Experience['type'] = item.type === 'education' ? 'education' : 'job';

        return {
          id: typeof item.id === 'number' ? item.id : index + 1,
          title: String(item.title),
          company: String(item.company || ''),
          description: item.description ? String(item.description) : undefined,
          technologies: Array.isArray(item.technologies)
            ? item.technologies.map((tech: any) => String(tech))
            : [],
          startDate: item.startDate ? String(item.startDate) : '2020-01',
          endDate: item.endDate === null || item.endDate === undefined ? null : String(item.endDate),
          location: String(item.location || 'Unknown'),
          type,
          grade: item.grade ? String(item.grade) : undefined
        };
      });
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const errorSections: string[] = [];

    try {
      const [
        profileResult,
        projectsResult,
        skillsResult,
        experiencesResult
      ] = await Promise.all([
        dataService.getProfileWithFallback(),
        dataService.getProjectsWithFallback(),
        dataService.getSkillsWithFallback(),
        dataService.getExperiencesWithFallback()
      ]);

      if (profileResult.success && profileResult.data) {
        setProfile({
          ...DEFAULT_PROFILE,
          ...(profileResult.data as Partial<Profile>)
        });
      } else {
        errorSections.push('profile');
        setProfile(DEFAULT_PROFILE);
      }

      if (projectsResult.success && projectsResult.data) {
        setProjects(transformProjects(projectsResult.data as any[]));
      } else {
        errorSections.push('projects');
        setProjects([]);
      }

      if (skillsResult.success && skillsResult.data) {
        setSkills(normalizeSkillsData(skillsResult.data));
      } else {
        errorSections.push('skills');
        setSkills([]);
      }

      if (experiencesResult.success && experiencesResult.data) {
        setExperiences(transformExperiences(experiencesResult.data as any[]));
      } else {
        errorSections.push('experience');
        setExperiences([]);
      }
    } catch (err) {
      console.error('Failed to load portfolio data', err);
      errorSections.push('portfolio');
      setProfile(DEFAULT_PROFILE);
      setProjects([]);
      setSkills([]);
      setExperiences([]);
    } finally {
      if (errorSections.length > 0) {
        const uniqueSections = Array.from(new Set(errorSections));
        setError(`Unable to load ${uniqueSections.join(', ')} data. Showing defaults where possible.`);
      } else {
        setError(null);
      }
      setIsLoading(false);
    }
  }, [normalizeSkillsData, transformExperiences, transformProjects]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const reload = useCallback(() => {
    loadData();
  }, [loadData]);

  const value: DataContextType = {
    profile,
    projects,
    skills,
    experiences,
    isLoading,
    error,
    reload
  };

  return (
    <DataContext.Provider value={value}>
      {isLoading ? (
        <LoadingState role="status" aria-live="polite">
          <Spinner aria-hidden="true" />
          <p>Loading site…</p>
        </LoadingState>
      ) : (
        <>
          {error && (
            <ErrorBanner role="alert">
              <span>{error}</span>
              <RetryButton type="button" onClick={reload}>
                Retry
              </RetryButton>
            </ErrorBanner>
          )}
          {children}
        </>
      )}
    </DataContext.Provider>
  );
};

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoadingState = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  background: linear-gradient(135deg, #0a0a0f, #111827 55%, #0a0a0f);
  color: #40e0ff;
  text-align: center;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 4px solid rgba(64, 224, 255, 0.2);
  border-top-color: #40e0ff;
  animation: ${spin} 1s linear infinite;
`;

const ErrorBanner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 0.85rem 1.5rem;
  background: rgba(64, 224, 255, 0.12);
  border-bottom: 1px solid rgba(64, 224, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: #40e0ff;
  font-size: 0.95rem;
  z-index: 1200;
  backdrop-filter: blur(12px);
  flex-wrap: wrap;
  text-align: center;
`;

const RetryButton = styled.button`
  background: rgba(64, 224, 255, 0.2);
  border: 1px solid rgba(64, 224, 255, 0.6);
  color: #40e0ff;
  padding: 0.35rem 0.95rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover,
  &:focus-visible {
    background: rgba(64, 224, 255, 0.3);
    border-color: #40e0ff;
    outline: none;
    transform: translateY(-1px);
  }
`;

